/**
 * 📊 표준 계정체계 (IFRS/GAAP 기반)
 *
 * 🎯 핵심 철학:
 * - IFRS/GAAP 표준 계정체계 준수
 * - 계층적 계정 구조 (대분류-중분류-소분류)
 * - 자동 계정코드 생성 및 검증
 *
 * ⚠️ 중요: 거래 가능 여부 규칙
 * - 상위 계정 (level 1): 분류/집계용만 사용, 실제 거래 불가 ❌
 * - 하위 계정 (level ≥ 2): 실제 거래 가능 ✅
 *
 * 이는 Aggregation Paradox를 방지하기 위한 필수 원칙입니다.
 * 상위 계정에 직접 거래를 입력하면 중복 집계 문제가 발생합니다.
 */

// ============================================
// 🎯 표준 계정체계 정의
// ============================================

export interface StandardAccount {
  code: string;
  name: string;
  category: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  subCategory: string;
  parentCode?: string;
  level: number; // 1: 대분류 (집계용), 2+: 중분류/소분류 (거래용)
  isActive: boolean;
  description: string;
  normalBalance: 'debit' | 'credit';
  requiresApproval: boolean;
  approvalThreshold?: number;

  /**
   * 거래 가능 여부
   * - level 1 (상위 계정): false (집계용만)
   * - level ≥ 2 (하위 계정): true (실제 거래 가능)
   *
   * 이 필드는 자동으로 계산되며, level 값에 따라 결정됩니다.
   */
  readonly allowsTransactions: boolean;
}

export interface ChartOfAccounts {
  version: string;
  standard: 'IFRS' | 'GAAP' | 'K-IFRS';
  effectiveDate: Date;
  accounts: StandardAccount[];
}

/**
 * 🎯 비즈니스 도메인별 허용 가능한 Revenue/Expense/Asset 계정 코드
 *
 * 각 비즈니스 도메인은 특정 Revenue/Expense/Asset 계정만 사용 가능
 * 예: sellable_item은 Sales Revenue(4000)만, asset_item은 Rental Income(4020)만
 */
export interface BusinessDomainAccountRestrictions {
  /** 비즈니스 도메인별 허용 가능한 Revenue 계정 코드 목록 */
  allowedRevenueAccounts: string[];
  /** 비즈니스 도메인별 허용 가능한 Expense 계정 코드 목록 */
  allowedExpenseAccounts: string[];
  /** 비즈니스 도메인별 허용 가능한 Asset 계정 코드 목록 (Item 도메인 전용) */
  allowedAssetAccounts?: string[];
}

/**
 * 📋 비즈니스 도메인별 계정 제한 정의
 *
 * ⚠️ 중요: 계정의 의미
 * - RevenueAccounts: **해당 비즈니스 도메인 객체가 직접적으로 발생시키는 수익**을 의미
 * - ExpenseAccounts: **해당 비즈니스 도메인 객체가 직접적으로 발생시키는 비용**을 의미
 * - AssetAccounts: **해당 비즈니스 도메인 객체가 직접적으로 보유하는 자산**을 의미
 *
 * 예시:
 * - External_Entity의 '5000' (Raw Materials Used)는 구매로 인한 비용이므로 External_entity에 포함되면 안됨
 * - Sellable_Item의 '4000' (Sales Revenue)는 판매 상품 객체가 직접 발생시키는 수익이므로 적절
 *
 * ✅ Level 1 계정만 설정: 사용자는 해당 Level 1 계정의 하위 Level 2+ 계정들을 선택 가능
 * 예: '6000' (Salaries and Wages) 설정 시 → '6001', '6002', '6003' 등 하위 계정 자동 허용
 */
export const BUSINESS_DOMAIN_ACCOUNT_RESTRICTIONS: Record<
  string,
  BusinessDomainAccountRestrictions
> = {
  // ============================================
  // Item 도메인 카테고리
  // ============================================

  /** 판매 상품: 판매 수익만 가능, Expense 없음 (qty 있음 → 재고 가치 감소량 = expense 자동 처리) */
  sellable_item: {
    allowedRevenueAccounts: [
      // ✅ 판매 상품 객체가 직접 발생시키는 수익: 판매 수익만
      '4000', // Sales Revenue (판매 수익) - level 1
    ],
    allowedExpenseAccounts: [], // ✅ Expense 없음: qty가 있으므로 재고 가치 감소량이 expense로 자동 처리됨
    allowedAssetAccounts: [
      // ✅ 판매 상품 객체가 직접 보유하는 자산: 재고자산만
      '1030', // Inventory (재고자산) - level 1
    ],
  },

  /** 서비스 상품: 서비스 수익만 가능, Expense 가능 (qty 없음), Asset 없음 */
  service_item: {
    allowedRevenueAccounts: [
      // ✅ 서비스 객체가 직접 발생시키는 수익: 서비스 수익만
      '4010', // Service Revenue (서비스 수익) - level 1
    ],
    allowedExpenseAccounts: [
      // ✅ 서비스 객체가 직접 발생시키는 비용: 서비스 제공에 필요한 직접 비용만
      '5010', // Direct Labor (서비스 제공 직접 노무비) - level 1
      '6010', // Rent Expense (서비스 제공 공간 비용) - level 1
      '6020', // Utilities (서비스 제공 유틸리티 비용) - level 1
    ],
    allowedAssetAccounts: [], // ✅ Asset 없음: qty가 없으므로 asset 없음
  },

  /** 자산 상품: 대여 수익만 가능, Expense 가능 (qty 없음 → quantity는 개념적으로 1로 처리, asset으로 인한 비용 발생 가능) */
  asset_item: {
    allowedRevenueAccounts: [
      // ✅ 자산 상품 객체가 직접 발생시키는 수익: 대여 수익만
      '4020', // Rental Income (대여 수익) - level 1
    ],
    allowedExpenseAccounts: [
      // ✅ 자산 상품 객체가 직접 발생시키는 비용: 자산 유지보수 및 감가상각 비용만
      '6030', // Depreciation Expense (감가상각비) - level 1
      '6040', // Repairs and Maintenance (수선비) - level 1
    ],
    allowedAssetAccounts: [
      // ✅ 자산 상품 객체가 직접 보유하는 자산: 유형자산 또는 무형자산만
      '1200', // Property, Plant, and Equipment (PPE) (유형자산) - level 1
      '1220', // Intangible Assets (무형자산) - level 1
    ],
  },

  /** 원자재: Revenue 없음 (판매 목적 아님), Expense 없음 (qty 있음 → 재고 가치 감소량 = expense 자동 처리) */
  raw_material_item: {
    allowedRevenueAccounts: [], // Revenue 없음: 판매 목적이 아니므로 수익 없음
    allowedExpenseAccounts: [], // ✅ Expense 없음: qty가 있으므로 재고 가치 감소량이 expense로 자동 처리됨
    allowedAssetAccounts: [
      // ✅ 원자재 객체가 직접 보유하는 자산: 재고자산만
      '1030', // Inventory (재고자산) - level 1
    ],
  },

  /** 소모품: Revenue 없음 (내부 사용), Expense 없음 (qty 있음 → 재고 가치 감소량 = expense 자동 처리) */
  consumable_item: {
    allowedRevenueAccounts: [], // Revenue 없음: 내부 사용이므로 수익 없음
    allowedExpenseAccounts: [], // ✅ Expense 없음: qty가 있으므로 재고 가치 감소량이 expense로 자동 처리됨
    allowedAssetAccounts: [
      // ✅ 소모품 객체가 직접 보유하는 자산: 재고자산만
      '1030', // Inventory (재고자산) - level 1
    ],
  },

  // ============================================
  // Space 도메인 카테고리
  // ============================================

  /** 물리적 공간: 대여 수익만 가능 */
  physical_space: {
    allowedRevenueAccounts: [
      // ✅ 물리적 공간 객체가 직접 발생시키는 수익: 대여 수익만
      '4020', // Rental Income (대여 수익) - level 1
    ],
    allowedExpenseAccounts: [
      // ✅ 물리적 공간 객체가 직접 발생시키는 비용: 공간 운영 비용만
      '6010', // Rent Expense (임차료) - level 1
      '6020', // Utilities (수도광열비) - level 1
      '6040', // Repairs and Maintenance (수선비) - level 1
      '6030', // Depreciation Expense (감가상각비) - level 1
      '6050', // Office Supplies (소모품비) - level 1
    ],
  },

  /** 가상 공간: Revenue 없음 (내부 사용) - 공간 운영 비용만 */
  virtual_space: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [
      // ✅ 가상 공간 객체가 직접 발생시키는 비용: 공간 운영 비용만
      '6010', // Rent Expense (가상 공간 운영 공간 비용) - level 1
      '6020', // Utilities (가상 공간 운영 유틸리티 비용) - level 1
      '6060', // Marketing and Advertising (팀 홍보 비용) - level 1
    ],
  },

  // ============================================
  // Entity 도메인 카테고리
  // ============================================

  /** 내부 엔티티: Revenue 없음 (고용 관계) - 인건비만 */
  internal_entity: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [
      '6000', // Salaries and Wages (급여 및 임금) - level 1만, 하위 계정(6001~6008) 자동 허용
    ],
  },

  /** 외부 엔티티: Revenue 없음 (고객/파트너 관계) - 관계 유지 비용만 */
  external_entity: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [
      // ✅ 외부 엔티티(고객/파트너)와의 관계 유지에 직접적으로 드는 비용만
      '6080', // Travel and Entertainment (고객/파트너 접대비) - level 1
      '6090', // Other Operating Expenses (고객/파트너 관리비) - level 1
    ],
  },

  // ============================================
  // Knowledge 도메인 카테고리
  // ============================================

  /** 지식 계획: Revenue 없음 (내부 지식 관리) - 지식 관리 비용만 */
  knowledge_plan: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [
      // ✅ 지식 계획 객체가 직접 발생시키는 비용: 지식 관리 비용만
      '6010', // Rent Expense (지식 계획 관련 공간 비용) - level 1
      '6050', // Office Supplies (지식 계획 관련 소모품비) - level 1
    ],
  },

  /** 지식 문서: Revenue 없음 (내부 지식 관리) - 지식 관리 비용만 */
  knowledge_document: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [
      // ✅ 지식 문서 객체가 직접 발생시키는 비용: 지식 관리 비용만
      '6010', // Rent Expense (지식 문서 관리 공간 비용) - level 1
      '6050', // Office Supplies (지식 문서 관련 소모품비) - level 1
    ],
  },

  // ============================================
  // Appeal 도메인 카테고리
  // ============================================

  /** 의미적 형성: 장기 정체성 (브랜드, 로고, 핵심 가치) - 기능 중심 */
  semantic_formation: {
    allowedRevenueAccounts: [], // Revenue 없음: 브랜드 자체는 수익을 직접 발생시키지 않음
    allowedExpenseAccounts: [
      // ✅ 의미적 형성 객체가 직접 발생시키는 비용: 브랜드 관리 비용만
      '6060', // Marketing and Advertising (브랜드 관리 마케팅 비용) - level 1
      '6070', // Research and Development (브랜드 개발 연구비) - level 1
    ],
    allowedAssetAccounts: [], // ✅ Asset 없음: 브랜드 가치는 asset_item으로 관리하고 relationField로 연결 (physical_space와 동일한 원칙)
  },

  /** 일시적 형성: 단기 소재 (광고물, 캠페인 소재) */
  ephemeral_formation: {
    allowedRevenueAccounts: [], // Revenue 없음: 광고물 자체는 수익을 직접 발생시키지 않음
    allowedExpenseAccounts: [
      // ✅ 일시적 형성 객체가 직접 발생시키는 비용: 광고 제작 비용만
      '6060', // Marketing and Advertising (광고 제작 비용) - level 1
    ],
    allowedAssetAccounts: [], // Asset 없음: 단기 소재이므로 자산으로 인식하지 않음
  },

  /** 반복 공연: 반복 행동 (뉴스레터, 정기 보고) */
  recurrent_performance: {
    allowedRevenueAccounts: [], // Revenue 없음: 뉴스레터 등은 직접 수익을 발생시키지 않음
    allowedExpenseAccounts: [
      // ✅ 반복 공연 객체가 직접 발생시키는 비용: 마케팅 비용만
      '6060', // Marketing and Advertising (뉴스레터/정기 보고 마케팅 비용) - level 1
      '6000', // Salaries and Wages (콘텐츠 제작 인건비) - level 1
    ],
    allowedAssetAccounts: [], // Asset 없음
  },

  /** 일시적 공연: 단발 행동 (이벤트, 프로모션) */
  ephemeral_performance: {
    allowedRevenueAccounts: [], // Revenue 없음: 이벤트 자체는 수익을 직접 발생시키지 않음
    allowedExpenseAccounts: [
      // ✅ 일시적 공연 객체가 직접 발생시키는 비용: 이벤트 운영 비용만
      '6060', // Marketing and Advertising (이벤트 마케팅 비용) - level 1
      '6080', // Travel and Entertainment (이벤트 운영 접대비) - level 1
      '6010', // Rent Expense (이벤트 공간 비용) - level 1
      '6020', // Utilities (이벤트 유틸리티 비용) - level 1
    ],
    allowedAssetAccounts: [], // Asset 없음
  },

  // ============================================
  // Finance 도메인 카테고리
  // ============================================

  /** 재무 상태: Revenue/Expense 없음 (잔액만 관리) */
  finance_state: {
    allowedRevenueAccounts: [], // Revenue 없음
    allowedExpenseAccounts: [], // Expense 없음
  },

  /** 재무 흐름: 모든 Revenue/Expense 계정 가능 (회계 기록) */
  finance_flow: {
    allowedRevenueAccounts: ['4000', '4010', '4020', '4030', '4040', '4050'], // 모든 Revenue 계정
    allowedExpenseAccounts: [
      '5000',
      '5010',
      '5020',
      '6000',
      '6010',
      '6020',
      '6030',
      '6040',
      '6050',
      '6060',
    ], // 모든 Expense 계정
  },
};

/**
 * 비즈니스 도메인별 허용 계정 코드 조회
 *
 * ✅ ValueField 구조를 기반으로 동적 결정:
 * - qty가 있는 도메인: expense 없음 (재고 가치 감소량 = expense 자동 처리)
 * - qty가 없는 도메인: expense 가능
 *
 * ✅ Level 1 계정의 하위 계정 자동 포함:
 * - Level 1 계정이 허용되면, 해당 계정의 모든 하위 Level 2+ 계정도 자동 허용
 */
// ============================================
// 🏦 표준 계정체계 (K-IFRS 기반)
// ============================================

/**
 * 계정 배열을 StandardAccount 타입으로 변환
 * 모든 계정에 allowsTransactions 자동 추가
 *
 * ⚠️ 중요: level 1 (상위 계정)은 거래 불가, level ≥ 2 (하위 계정)만 거래 가능
 */
function processAccounts(
  accounts: Omit<StandardAccount, 'allowsTransactions'>[]
): StandardAccount[] {
  return accounts.map((account) => ({
    ...account,
    allowsTransactions: account.level >= 2, // level 1은 거래 불가, level 2+는 거래 가능
  }));
}

// 원시 계정 데이터 (allowsTransactions 제외)
const RAW_ACCOUNTS: Omit<StandardAccount, 'allowsTransactions'>[] = [
  // ============================================
  // 💰 자산 (Assets) - 1xxx
  // ============================================

  // === 현금 및 현금성 자산 (1000-1099) ===
  // 1000 - Cash and Cash Equivalents (집계용, 거래 불가)
  {
    code: '1000',
    name: 'Cash and Cash Equivalents',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '현금 및 현금성 자산',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1001',
    name: 'Cash on Hand',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '현금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1000',
  },
  {
    code: '1002',
    name: 'Cash in Bank',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '예금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1000',
  },
  {
    code: '1003',
    name: 'Petty Cash',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '소액현금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1000',
  },
  {
    code: '1004',
    name: 'Time Deposits',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '정기예금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1000',
  },
  {
    code: '1005',
    name: 'Marketable Securities',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '유가증권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '1000',
  },

  // === 매출채권 및 기타 채권 (1010-1099) ===
  // 1010 - Accounts Receivable
  {
    code: '1010',
    name: 'Accounts Receivable',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '매출채권',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1011',
    name: 'Trade Receivables',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매출채권',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1010',
  },
  {
    code: '1012',
    name: 'Allowance for Doubtful Accounts',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '대손충당금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1010',
  },
  {
    code: '1013',
    name: 'Other Receivables',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '기타채권',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1010',
  },
  {
    code: '1014',
    name: 'Advances to Suppliers',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선급금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1010',
  },
  {
    code: '1015',
    name: 'Employee Advances',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '사원대여금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1010',
  },

  // 1020 - Notes Receivable
  {
    code: '1020',
    name: 'Notes Receivable',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '받을어음',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1021',
    name: 'Trade Notes Receivable',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매출어음',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1020',
  },
  {
    code: '1022',
    name: 'Other Notes Receivable',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '기타어음',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1020',
  },

  // === 재고자산 (1030-1099) ===
  // 1030 - Inventory
  {
    code: '1030',
    name: 'Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '재고자산',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1031',
    name: 'Raw Materials Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '원재료재고',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1030',
  },
  {
    code: '1032',
    name: 'Work in Process Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '제품재고',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1030',
  },
  {
    code: '1033',
    name: 'Finished Goods Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '완제품재고',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1030',
  },
  {
    code: '1034',
    name: 'Merchandise Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '상품재고',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1030',
  },
  {
    code: '1035',
    name: 'Supplies Inventory',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '소모품재고',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1030',
  },
  {
    code: '1036',
    name: 'Inventory Valuation Reserve',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '재고평가충당금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1030',
  },

  // === 선급비용 및 기타 유동자산 (1040-1099) ===
  // 1040 - Prepaid Expenses
  {
    code: '1040',
    name: 'Prepaid Expenses',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '선급비용',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1041',
    name: 'Prepaid Rent',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선급임차료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1040',
  },
  {
    code: '1042',
    name: 'Prepaid Insurance',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선급보험료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1040',
  },
  {
    code: '1043',
    name: 'Prepaid Taxes',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선급세금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1040',
  },
  {
    code: '1044',
    name: 'Prepaid Utilities',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선급공과금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1040',
  },

  // 1050 - Short-Term Investments
  {
    code: '1050',
    name: 'Short-Term Investments',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '단기금융상품',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
  },
  {
    code: '1051',
    name: 'Trading Securities',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매매증권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '1050',
  },
  {
    code: '1052',
    name: 'Available-for-Sale Securities',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매도가능증권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '1050',
  },

  // 1060 - Other Current Assets
  {
    code: '1060',
    name: 'Other Current Assets',
    category: 'asset',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '기타 유동자산',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1061',
    name: 'Accrued Revenue',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미수수익',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1060',
  },
  {
    code: '1062',
    name: 'Tax Refund Receivable',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '세금환급금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1060',
  },
  {
    code: '1063',
    name: 'Deposits',
    category: 'asset',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '예치금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1060',
  },

  // === 유형자산 (1200-1299) ===
  // 1200 - Property, Plant, and Equipment (PPE)
  {
    code: '1200',
    name: 'Property, Plant, and Equipment (PPE)',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '유형자산',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
  },
  {
    code: '1201',
    name: 'Land',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '토지',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1200',
  },
  {
    code: '1202',
    name: 'Buildings',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '건물',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1200',
  },
  {
    code: '1203',
    name: 'Machinery and Equipment',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '기계장치',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1200',
  },
  {
    code: '1204',
    name: 'Vehicles',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '차량운반구',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1200',
  },
  {
    code: '1205',
    name: 'Furniture and Fixtures',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '비품',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '1200',
  },
  {
    code: '1206',
    name: 'Computer Equipment',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '컴퓨터장비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '1200',
  },
  {
    code: '1207',
    name: 'Construction in Progress',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '건설중인자산',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1200',
  },

  // 1210 - Accumulated Depreciation
  {
    code: '1210',
    name: 'Accumulated Depreciation',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '감가상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '1211',
    name: 'Accumulated Depreciation - Buildings',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '건물감가상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1210',
  },
  {
    code: '1212',
    name: 'Accumulated Depreciation - Machinery',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '기계장치감가상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1210',
  },
  {
    code: '1213',
    name: 'Accumulated Depreciation - Vehicles',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '차량감가상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1210',
  },
  {
    code: '1214',
    name: 'Accumulated Depreciation - Equipment',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '비품감가상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1210',
  },

  // === 무형자산 (1220-1239) ===
  // 1220 - Intangible Assets
  {
    code: '1220',
    name: 'Intangible Assets',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '무형자산',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
  },
  {
    code: '1221',
    name: 'Patents',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '특허권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1220',
  },
  {
    code: '1222',
    name: 'Trademarks',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '상표권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1220',
  },
  {
    code: '1223',
    name: 'Copyrights',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '저작권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1220',
  },
  {
    code: '1224',
    name: 'Software',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '소프트웨어',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1220',
  },
  {
    code: '1225',
    name: 'Licenses',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '라이선스',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 3000000,
    parentCode: '1220',
  },
  {
    code: '1226',
    name: 'Accumulated Amortization - Intangibles',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '무형자산상각누계액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '1220',
  },

  // 1230 - Goodwill
  {
    code: '1230',
    name: 'Goodwill',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '영업권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 10000000,
  },

  // === 투자자산 (1240-1259) ===
  // 1240 - Long-Term Investments
  {
    code: '1240',
    name: 'Long-Term Investments',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '장기투자자산',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
  },
  {
    code: '1241',
    name: 'Investment in Subsidiaries',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '관계기업투자',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1240',
  },
  {
    code: '1242',
    name: 'Investment in Associates',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '종속기업투자',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1240',
  },
  {
    code: '1243',
    name: 'Investment Securities',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '투자유가증권',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '1240',
  },

  // === 기타 비유동자산 (1250-1299) ===
  // 1250 - Deferred Tax Assets
  {
    code: '1250',
    name: 'Deferred Tax Assets',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '이연법인세자산',
    normalBalance: 'debit',
    requiresApproval: false,
  },

  // 1260 - Right-of-Use Assets (IFRS 16)
  {
    code: '1260',
    name: 'Right-of-Use Assets',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '사용권자산 (IFRS 16)',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
  },
  {
    code: '1261',
    name: 'Right-of-Use Assets - Buildings',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '사용권자산-건물',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '1260',
  },
  {
    code: '1262',
    name: 'Right-of-Use Assets - Equipment',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '사용권자산-장비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '1260',
  },

  // 1270 - Other Non-Current Assets
  {
    code: '1270',
    name: 'Other Non-Current Assets',
    category: 'asset',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '기타 비유동자산',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '1271',
    name: 'Long-Term Prepaid Expenses',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '장기선급비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1270',
  },
  {
    code: '1272',
    name: 'Long-Term Deposits',
    category: 'asset',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '장기예치금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '1270',
  },

  // ============================================
  // 💳 부채 (Liabilities) - 2xxx
  // ============================================

  // === 매입채무 및 기타 채무 (2000-2099) ===
  // 2000 - Accounts Payable
  {
    code: '2000',
    name: 'Accounts Payable',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '매입채무',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2001',
    name: 'Trade Payables',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매입채무',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2000',
  },
  {
    code: '2002',
    name: 'Other Payables',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '기타채무',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2000',
  },
  {
    code: '2003',
    name: 'Advances from Customers',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선수금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2000',
  },

  // 2010 - Notes Payable
  {
    code: '2010',
    name: 'Notes Payable',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '지급어음',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2011',
    name: 'Trade Notes Payable',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '매입어음',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2010',
  },
  {
    code: '2012',
    name: 'Other Notes Payable',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '기타어음',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2010',
  },

  // 2020 - Accrued Expenses
  {
    code: '2020',
    name: 'Accrued Expenses',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '미지급비용',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2021',
    name: 'Accrued Salaries',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미지급급여',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2020',
  },
  {
    code: '2022',
    name: 'Accrued Interest',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미지급이자',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2020',
  },
  {
    code: '2023',
    name: 'Accrued Taxes',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미지급세금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2020',
  },
  {
    code: '2024',
    name: 'Accrued Utilities',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미지급공과금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2020',
  },
  {
    code: '2025',
    name: 'Accrued Rent',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '미지급임차료',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2020',
  },

  // 2030 - Short-Term Borrowings
  {
    code: '2030',
    name: 'Short-Term Borrowings',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '단기차입금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 10000000,
  },
  {
    code: '2031',
    name: 'Bank Loans - Short Term',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '단기은행차입금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 10000000,
    parentCode: '2030',
  },
  {
    code: '2032',
    name: 'Commercial Paper',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '단기사채',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 10000000,
    parentCode: '2030',
  },

  // 2040 - Income Tax Payable
  {
    code: '2040',
    name: 'Income Tax Payable',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '법인세부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2041',
    name: 'Corporate Income Tax Payable',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '법인세부채',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2040',
  },
  {
    code: '2042',
    name: 'Withholding Tax Payable',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '원천징수세금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2040',
  },

  // 2050 - Current Portion of Long-Term Debt
  {
    code: '2050',
    name: 'Current Portion of Long-Term Debt',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '유동성장기부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },

  // 2060 - Unearned Revenue
  {
    code: '2060',
    name: 'Unearned Revenue',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '선수수익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2061',
    name: 'Unearned Service Revenue',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선수용역수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2060',
  },
  {
    code: '2062',
    name: 'Unearned Rental Income',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '선수임대수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2060',
  },

  // 2070 - Lease Liabilities (Current)
  {
    code: '2070',
    name: 'Lease Liabilities (Current)',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '리스부채 (유동)',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 2000000,
  },

  // 2080 - Other Current Liabilities
  {
    code: '2080',
    name: 'Other Current Liabilities',
    category: 'liability',
    subCategory: 'current',
    level: 1,
    isActive: true,
    description: '기타 유동부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2081',
    name: 'Dividends Payable',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '배당금지급예정액',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2080',
  },
  {
    code: '2082',
    name: 'Warranty Liability',
    category: 'liability',
    subCategory: 'current',
    level: 2,
    isActive: true,
    description: '보증부채',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2080',
  },

  // === 장기부채 (2200-2299) ===
  // 2200 - Long-Term Debt
  {
    code: '2200',
    name: 'Long-Term Debt',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '장기차입금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 20000000,
  },
  {
    code: '2201',
    name: 'Bank Loans - Long Term',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '장기은행차입금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 20000000,
    parentCode: '2200',
  },
  {
    code: '2202',
    name: 'Bonds Payable',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '사채',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 20000000,
    parentCode: '2200',
  },
  {
    code: '2203',
    name: 'Mortgage Payable',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '담보부채',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 20000000,
    parentCode: '2200',
  },

  // 2210 - Lease Liabilities (Non-Current)
  {
    code: '2210',
    name: 'Lease Liabilities (Non-Current)',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '리스부채 (비유동)',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 5000000,
  },

  // 2220 - Pension Liabilities
  {
    code: '2220',
    name: 'Pension Liabilities',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '퇴직급여부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '2221',
    name: 'Defined Benefit Pension Liability',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '확정급여퇴직급여부채',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2220',
  },
  {
    code: '2222',
    name: 'Severance Pay Liability',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '퇴직금부채',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '2220',
  },

  // 2230 - Deferred Tax Liabilities
  {
    code: '2230',
    name: 'Deferred Tax Liabilities',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '이연법인세부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },

  // 2240 - Provision for Contingencies
  {
    code: '2240',
    name: 'Provision for Contingencies',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '충당부채',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 1000000,
  },
  {
    code: '2241',
    name: 'Warranty Provision',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '보증충당금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '2240',
  },
  {
    code: '2242',
    name: 'Litigation Provision',
    category: 'liability',
    subCategory: 'non-current',
    level: 2,
    isActive: true,
    description: '소송충당금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '2240',
  },

  // 2250 - Other Non-Current Liabilities
  {
    code: '2250',
    name: 'Other Non-Current Liabilities',
    category: 'liability',
    subCategory: 'non-current',
    level: 1,
    isActive: true,
    description: '기타 비유동부채',
    normalBalance: 'credit',
    requiresApproval: false,
  },

  // ============================================
  // 👑 자본 (Equity) - 3xxx
  // ============================================

  // === 자본금 (3000-3019) ===
  // 3000 - Capital Stock / Share Capital
  {
    code: '3000',
    name: 'Capital Stock / Share Capital',
    category: 'equity',
    subCategory: 'capital',
    level: 1,
    isActive: true,
    description: '자본금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 100000000,
  },
  {
    code: '3001',
    name: 'Common Stock',
    category: 'equity',
    subCategory: 'capital',
    level: 2,
    isActive: true,
    description: '보통주',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 100000000,
    parentCode: '3000',
  },
  {
    code: '3002',
    name: 'Preferred Stock',
    category: 'equity',
    subCategory: 'capital',
    level: 2,
    isActive: true,
    description: '우선주',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 100000000,
    parentCode: '3000',
  },

  // 3010 - Additional Paid-in Capital
  {
    code: '3010',
    name: 'Additional Paid-in Capital',
    category: 'equity',
    subCategory: 'capital',
    level: 1,
    isActive: true,
    description: '주식발행초과금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 50000000,
  },
  {
    code: '3011',
    name: 'Paid-in Capital in Excess of Par',
    category: 'equity',
    subCategory: 'capital',
    level: 2,
    isActive: true,
    description: '주식발행초과금',
    normalBalance: 'credit',
    requiresApproval: true,
    approvalThreshold: 50000000,
    parentCode: '3010',
  },
  {
    code: '3012',
    name: 'Share Premium',
    category: 'equity',
    subCategory: 'capital',
    level: 2,
    isActive: true,
    description: '주식할인발행차금',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 50000000,
    parentCode: '3010',
  },

  // === 이익잉여금 (3020-3039) ===
  // 3020 - Retained Earnings
  {
    code: '3020',
    name: 'Retained Earnings',
    category: 'equity',
    subCategory: 'retained',
    level: 1,
    isActive: true,
    description: '이익잉여금',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '3021',
    name: 'Appropriated Retained Earnings',
    category: 'equity',
    subCategory: 'retained',
    level: 2,
    isActive: true,
    description: '이익준비금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '3020',
  },
  {
    code: '3022',
    name: 'Unappropriated Retained Earnings',
    category: 'equity',
    subCategory: 'retained',
    level: 2,
    isActive: true,
    description: '미처분이익잉여금',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '3020',
  },

  // 3030 - Other Comprehensive Income
  {
    code: '3030',
    name: 'Other Comprehensive Income',
    category: 'equity',
    subCategory: 'comprehensive',
    level: 1,
    isActive: true,
    description: '기타포괄손익누계액',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '3031',
    name: 'Unrealized Gain/Loss on Securities',
    category: 'equity',
    subCategory: 'comprehensive',
    level: 2,
    isActive: true,
    description: '기타포괄손익-증권평가손익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '3030',
  },
  {
    code: '3032',
    name: 'Foreign Currency Translation',
    category: 'equity',
    subCategory: 'comprehensive',
    level: 2,
    isActive: true,
    description: '기타포괄손익-환율변동',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '3030',
  },

  // === 기타 자본 (3040-3059) ===
  // 3040 - Treasury Stock
  {
    code: '3040',
    name: 'Treasury Stock',
    category: 'equity',
    subCategory: 'treasury',
    level: 1,
    isActive: true,
    description: '자기주식',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 20000000,
  },

  // 3050 - Non-controlling Interests
  {
    code: '3050',
    name: 'Non-controlling Interests',
    category: 'equity',
    subCategory: 'minority',
    level: 1,
    isActive: true,
    description: '비지배지분',
    normalBalance: 'credit',
    requiresApproval: false,
  },

  // ============================================
  // 💰 수익 (Revenue) - 4xxx
  // ============================================

  // === 매출수익 (4000-4099) ===
  // 4000 - Sales Revenue
  {
    code: '4000',
    name: 'Sales Revenue',
    category: 'revenue',
    subCategory: 'sales',
    level: 1,
    isActive: true,
    description: '제품/상품 매출',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '4001',
    name: 'Product Sales',
    category: 'revenue',
    subCategory: 'sales',
    level: 2,
    isActive: true,
    description: '제품매출',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4000',
  },
  {
    code: '4002',
    name: 'Merchandise Sales',
    category: 'revenue',
    subCategory: 'sales',
    level: 2,
    isActive: true,
    description: '상품매출',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4000',
  },
  {
    code: '4003',
    name: 'Sales Returns',
    category: 'revenue',
    subCategory: 'sales',
    level: 2,
    isActive: true,
    description: '매출반품',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '4000',
  },
  {
    code: '4004',
    name: 'Sales Allowances',
    category: 'revenue',
    subCategory: 'sales',
    level: 2,
    isActive: true,
    description: '매출할인',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '4000',
  },
  {
    code: '4005',
    name: 'Sales Discounts',
    category: 'revenue',
    subCategory: 'sales',
    level: 2,
    isActive: true,
    description: '매출할인',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '4000',
  },

  // 4010 - Service Revenue
  {
    code: '4010',
    name: 'Service Revenue',
    category: 'revenue',
    subCategory: 'service',
    level: 1,
    isActive: true,
    description: '용역매출',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '4011',
    name: 'Consulting Revenue',
    category: 'revenue',
    subCategory: 'service',
    level: 2,
    isActive: true,
    description: '컨설팅수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4010',
  },
  {
    code: '4012',
    name: 'Maintenance Service Revenue',
    category: 'revenue',
    subCategory: 'service',
    level: 2,
    isActive: true,
    description: '유지보수수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4010',
  },
  {
    code: '4013',
    name: 'Subscription Revenue',
    category: 'revenue',
    subCategory: 'service',
    level: 2,
    isActive: true,
    description: '구독수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4010',
  },
  {
    code: '4014',
    name: 'Commission Revenue',
    category: 'revenue',
    subCategory: 'service',
    level: 2,
    isActive: true,
    description: '수수료수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4010',
  },

  // 4020 - Rental Income
  {
    code: '4020',
    name: 'Rental Income',
    category: 'revenue',
    subCategory: 'rental',
    level: 1,
    isActive: true,
    description: '임대수익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '4021',
    name: 'Property Rental Income',
    category: 'revenue',
    subCategory: 'rental',
    level: 2,
    isActive: true,
    description: '부동산임대수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4020',
  },
  {
    code: '4022',
    name: 'Equipment Rental Income',
    category: 'revenue',
    subCategory: 'rental',
    level: 2,
    isActive: true,
    description: '장비임대수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4020',
  },

  // 4030 - Interest Income
  {
    code: '4030',
    name: 'Interest Income',
    category: 'revenue',
    subCategory: 'interest',
    level: 1,
    isActive: true,
    description: '이자수익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '4031',
    name: 'Interest on Loans',
    category: 'revenue',
    subCategory: 'interest',
    level: 2,
    isActive: true,
    description: '대출이자수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4030',
  },
  {
    code: '4032',
    name: 'Interest on Investments',
    category: 'revenue',
    subCategory: 'interest',
    level: 2,
    isActive: true,
    description: '투자이자수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4030',
  },

  // 4040 - Dividend Income
  {
    code: '4040',
    name: 'Dividend Income',
    category: 'revenue',
    subCategory: 'dividend',
    level: 1,
    isActive: true,
    description: '배당수익',
    normalBalance: 'credit',
    requiresApproval: false,
  },

  // 4050 - Other Income
  {
    code: '4050',
    name: 'Other Income',
    category: 'revenue',
    subCategory: 'other',
    level: 1,
    isActive: true,
    description: '기타수익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '4051',
    name: 'Gain on Foreign Exchange',
    category: 'revenue',
    subCategory: 'other',
    level: 2,
    isActive: true,
    description: '환차익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4050',
  },
  {
    code: '4052',
    name: 'Miscellaneous Income',
    category: 'revenue',
    subCategory: 'other',
    level: 2,
    isActive: true,
    description: '잡수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '4050',
  },

  // ============================================
  // 💸 비용 (Expenses) - 5xxx
  // ============================================

  // === 매출원가 (COGS) (5000-5099) ===
  // 5000 - Raw Materials Used
  {
    code: '5000',
    name: 'Raw Materials Used',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '원재료비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5001',
    name: 'Direct Materials',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '직접재료비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5000',
  },
  {
    code: '5002',
    name: 'Indirect Materials',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '간접재료비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5000',
  },
  {
    code: '5003',
    name: 'Packaging Materials',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '포장재료비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5000',
  },

  // 5010 - Direct Labor
  {
    code: '5010',
    name: 'Direct Labor',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '직접노무비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5011',
    name: 'Production Labor',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '생산직노무비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5010',
  },
  {
    code: '5012',
    name: 'Assembly Labor',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '조립노무비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5010',
  },

  // 5020 - Manufacturing Overhead
  {
    code: '5020',
    name: 'Manufacturing Overhead',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '제조경비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5021',
    name: 'Factory Utilities',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '공장공과금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5020',
  },
  {
    code: '5022',
    name: 'Factory Rent',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '공장임차료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5020',
  },
  {
    code: '5023',
    name: 'Factory Depreciation',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '공장감가상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5020',
  },
  {
    code: '5024',
    name: 'Indirect Labor',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '간접노무비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5020',
  },

  // 5030 - Purchase Cost
  {
    code: '5030',
    name: 'Purchase Cost',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '매입원가',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5031',
    name: 'Merchandise Purchase',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '상품매입',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5030',
  },
  {
    code: '5032',
    name: 'Product Purchase',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '제품매입',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5030',
  },

  // 5040 - Freight In
  {
    code: '5040',
    name: 'Freight In',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '운반비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5041',
    name: 'Shipping Costs',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '운송비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5040',
  },
  {
    code: '5042',
    name: 'Delivery Costs',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '배송비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5040',
  },

  // 5050 - Inventory Adjustment
  {
    code: '5050',
    name: 'Inventory Adjustment',
    category: 'expense',
    subCategory: 'cogs',
    level: 1,
    isActive: true,
    description: '재고조정손익',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '5051',
    name: 'Inventory Write-Down',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '재고평가손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5050',
  },
  {
    code: '5052',
    name: 'Inventory Obsolescence',
    category: 'expense',
    subCategory: 'cogs',
    level: 2,
    isActive: true,
    description: '재고폐기손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '5050',
  },

  // ============================================
  // 💼 영업비용 (Operating Expenses) - 6xxx
  // ============================================

  // === 인건비 (6000-6099) ===
  // 6000 - Salaries and Wages
  {
    code: '6000',
    name: 'Salaries and Wages',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '급여 및 임금',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6001',
    name: 'Salaries',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '급여',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6002',
    name: 'Wages',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '임금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6003',
    name: 'Overtime Pay',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '초과근무수당',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6004',
    name: 'Bonuses',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '상여금',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 5000000,
    parentCode: '6000',
  },
  {
    code: '6005',
    name: 'Employee Benefits',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '복리후생비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6006',
    name: 'Social Security Contributions',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '사회보험료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6007',
    name: 'Retirement Benefits',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '퇴직급여',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },
  {
    code: '6008',
    name: 'Training and Development',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '교육훈련비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6000',
  },

  // 6010 - Rent Expense
  {
    code: '6010',
    name: 'Rent Expense',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '임차료',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6011',
    name: 'Office Rent',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '사무실임차료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6010',
  },
  {
    code: '6012',
    name: 'Warehouse Rent',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '창고임차료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6010',
  },
  {
    code: '6013',
    name: 'Store Rent',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '매장임차료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6010',
  },

  // 6020 - Utilities
  {
    code: '6020',
    name: 'Utilities',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '수도광열비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6021',
    name: 'Electricity',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '전기료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6020',
  },
  {
    code: '6022',
    name: 'Water',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '수도료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6020',
  },
  {
    code: '6023',
    name: 'Gas',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '가스료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6020',
  },
  {
    code: '6024',
    name: 'Heating',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '난방비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6020',
  },
  {
    code: '6025',
    name: 'Internet and Communications',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '통신비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6020',
  },

  // 6030 - Depreciation Expense
  {
    code: '6030',
    name: 'Depreciation Expense',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '감가상각비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6031',
    name: 'Depreciation - Buildings',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '건물감가상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6030',
  },
  {
    code: '6032',
    name: 'Depreciation - Equipment',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '장비감가상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6030',
  },
  {
    code: '6033',
    name: 'Depreciation - Vehicles',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '차량감가상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6030',
  },
  {
    code: '6034',
    name: 'Amortization - Intangibles',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '무형자산상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6030',
  },

  // 6040 - Repairs and Maintenance
  {
    code: '6040',
    name: 'Repairs and Maintenance',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '수선비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6041',
    name: 'Building Maintenance',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '건물수선비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6040',
  },
  {
    code: '6042',
    name: 'Equipment Maintenance',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '장비수선비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6040',
  },
  {
    code: '6043',
    name: 'Vehicle Maintenance',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '차량수선비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6040',
  },
  {
    code: '6044',
    name: 'IT Maintenance',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: 'IT유지보수비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6040',
  },

  // 6050 - Office Supplies
  {
    code: '6050',
    name: 'Office Supplies',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '소모품비',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6051',
    name: 'Stationery',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '문구비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6050',
  },
  {
    code: '6052',
    name: 'Cleaning Supplies',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '청소용품비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6050',
  },
  {
    code: '6053',
    name: 'Printing and Copying',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '인쇄복사비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6050',
  },

  // === 판매비용 (6060-6099) ===
  // 6060 - Marketing and Advertising
  {
    code: '6060',
    name: 'Marketing and Advertising',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '광고선전비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
  },
  {
    code: '6061',
    name: 'Advertising',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '광고비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '6060',
  },
  {
    code: '6062',
    name: 'Promotional Expenses',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '홍보비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '6060',
  },
  {
    code: '6063',
    name: 'Digital Marketing',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '디지털마케팅비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '6060',
  },
  {
    code: '6064',
    name: 'Sales Promotion',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '판촉비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '6060',
  },

  // 6070 - Research and Development
  {
    code: '6070',
    name: 'Research and Development',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '연구개발비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
  },
  {
    code: '6071',
    name: 'Research Expenses',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '연구비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '6070',
  },
  {
    code: '6072',
    name: 'Development Expenses',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '개발비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '6070',
  },
  {
    code: '6073',
    name: 'R&D Personnel Costs',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '연구개발인건비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 2000000,
    parentCode: '6070',
  },

  // 6080 - Travel and Entertainment
  {
    code: '6080',
    name: 'Travel and Entertainment',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '접대비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 500000,
  },
  {
    code: '6081',
    name: 'Business Travel',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '출장비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 500000,
    parentCode: '6080',
  },
  {
    code: '6082',
    name: 'Entertainment',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '접대비',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 500000,
    parentCode: '6080',
  },
  {
    code: '6083',
    name: 'Meals and Entertainment',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '식대',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 500000,
    parentCode: '6080',
  },

  // 6090 - Other Operating Expenses
  {
    code: '6090',
    name: 'Other Operating Expenses',
    category: 'expense',
    subCategory: 'operating',
    level: 1,
    isActive: true,
    description: '기타영업비용',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '6091',
    name: 'Insurance Expense',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '보험료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6092',
    name: 'Professional Fees',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '전문가자문비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6093',
    name: 'Legal Fees',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '법무비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6094',
    name: 'Accounting Fees',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '회계비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6095',
    name: 'Bank Charges',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '은행수수료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6096',
    name: 'Bad Debt Expense',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '대손상각비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6097',
    name: 'Freight Out',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '판매운반비',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },
  {
    code: '6098',
    name: 'Sales Commission',
    category: 'expense',
    subCategory: 'operating',
    level: 2,
    isActive: true,
    description: '판매수수료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '6090',
  },

  // ============================================
  // 🔄 영업외손익 (Non-Operating) - 7xxx
  // ============================================

  // === 영업외비용 (7000-7099) ===
  // 7000 - Interest Expense
  {
    code: '7000',
    name: 'Interest Expense',
    category: 'expense',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '이자비용',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '7001',
    name: 'Interest on Loans',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '차입이자비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7000',
  },
  {
    code: '7002',
    name: 'Interest on Bonds',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '사채이자비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7000',
  },
  {
    code: '7003',
    name: 'Bank Charges - Interest',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '은행이자비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7000',
  },

  // 7010 - Gain on Sale of Asset
  {
    code: '7010',
    name: 'Gain on Sale of Asset',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '자산처분이익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '7011',
    name: 'Gain on Sale of Fixed Assets',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '유형자산처분이익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7010',
  },
  {
    code: '7012',
    name: 'Gain on Sale of Investments',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '투자자산처분이익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7010',
  },

  // 7020 - Loss on Sale of Asset
  {
    code: '7020',
    name: 'Loss on Sale of Asset',
    category: 'expense',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '자산처분손실',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '7021',
    name: 'Loss on Sale of Fixed Assets',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '유형자산처분손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7020',
  },
  {
    code: '7022',
    name: 'Loss on Sale of Investments',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '투자자산처분손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7020',
  },
  {
    code: '7023',
    name: 'Impairment Loss',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '자산손상차손',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7020',
  },

  // 7030 - Foreign Exchange Gain
  {
    code: '7030',
    name: 'Foreign Exchange Gain',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '환차익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '7031',
    name: 'FX Gain on Receivables',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '외화채권환차익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7030',
  },
  {
    code: '7032',
    name: 'FX Gain on Payables',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '외화채무환차익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7030',
  },

  // 7040 - Foreign Exchange Loss
  {
    code: '7040',
    name: 'Foreign Exchange Loss',
    category: 'expense',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '환차손',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '7041',
    name: 'FX Loss on Receivables',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '외화채권환차손',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7040',
  },
  {
    code: '7042',
    name: 'FX Loss on Payables',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '외화채무환차손',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7040',
  },

  // 7050 - Gain/Loss on Investment
  {
    code: '7050',
    name: 'Gain/Loss on Investment',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '투자손익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '7051',
    name: 'Gain on Investment',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '투자이익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7050',
  },
  {
    code: '7052',
    name: 'Loss on Investment',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '투자손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7050',
  },
  {
    code: '7053',
    name: 'Unrealized Gain on Securities',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '증권평가이익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7050',
  },
  {
    code: '7054',
    name: 'Unrealized Loss on Securities',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '증권평가손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7050',
  },

  // 7060 - Other Non-Operating
  {
    code: '7060',
    name: 'Other Non-Operating',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 1,
    isActive: true,
    description: '기타영업외손익',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '7061',
    name: 'Other Non-Operating Income',
    category: 'revenue',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '기타영업외수익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '7060',
  },
  {
    code: '7062',
    name: 'Other Non-Operating Expense',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '기타영업외비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7060',
  },
  {
    code: '7063',
    name: 'Donations',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '기부금',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 1000000,
    parentCode: '7060',
  },
  {
    code: '7064',
    name: 'Penalties and Fines',
    category: 'expense',
    subCategory: 'non-operating',
    level: 2,
    isActive: true,
    description: '과태료',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '7060',
  },

  // ============================================
  // 🏛️ 법인세 (Income Taxes) - 8xxx
  // ============================================

  // === 법인세비용 (8000-8099) ===
  // 8000 - Income Tax Expense
  {
    code: '8000',
    name: 'Income Tax Expense',
    category: 'expense',
    subCategory: 'tax',
    level: 1,
    isActive: true,
    description: '법인세비용',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '8001',
    name: 'Current Tax Expense',
    category: 'expense',
    subCategory: 'tax',
    level: 2,
    isActive: true,
    description: '당기법인세비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '8000',
  },
  {
    code: '8002',
    name: 'Deferred Tax Expense',
    category: 'expense',
    subCategory: 'tax',
    level: 2,
    isActive: true,
    description: '이연법인세비용',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '8000',
  },
  {
    code: '8003',
    name: 'Tax Penalties',
    category: 'expense',
    subCategory: 'tax',
    level: 2,
    isActive: true,
    description: '세금가산금',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '8000',
  },

  // 8010 - Deferred Tax Adjustment
  {
    code: '8010',
    name: 'Deferred Tax Adjustment',
    category: 'expense',
    subCategory: 'tax',
    level: 1,
    isActive: true,
    description: '이연법인세조정',
    normalBalance: 'debit',
    requiresApproval: false,
  },
  {
    code: '8011',
    name: 'Deferred Tax Asset Amortization',
    category: 'expense',
    subCategory: 'tax',
    level: 2,
    isActive: true,
    description: '이연법인세자산상각',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '8010',
  },
  {
    code: '8012',
    name: 'Deferred Tax Liability Recognition',
    category: 'expense',
    subCategory: 'tax',
    level: 2,
    isActive: true,
    description: '이연법인세부채인식',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '8010',
  },

  // ============================================
  // 📊 당기순이익 (Net Income) - 9xxx
  // ============================================

  // === 당기순이익 (9000-9099) ===
  // 9000 - Net Profit or Loss
  {
    code: '9000',
    name: 'Net Profit or Loss',
    category: 'equity',
    subCategory: 'net-income',
    level: 1,
    isActive: true,
    description: '당기순이익(손실)',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '9001',
    name: 'Net Income',
    category: 'equity',
    subCategory: 'net-income',
    level: 2,
    isActive: true,
    description: '당기순이익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '9000',
  },
  {
    code: '9002',
    name: 'Net Loss',
    category: 'equity',
    subCategory: 'net-income',
    level: 2,
    isActive: true,
    description: '당기순손실',
    normalBalance: 'debit',
    requiresApproval: false,
    parentCode: '9000',
  },
  {
    code: '9003',
    name: 'Comprehensive Income',
    category: 'equity',
    subCategory: 'net-income',
    level: 2,
    isActive: true,
    description: '포괄손익',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '9000',
  },

  // 9010 - Retained Earnings Transfer
  {
    code: '9010',
    name: 'Retained Earnings Transfer',
    category: 'equity',
    subCategory: 'retained',
    level: 1,
    isActive: true,
    description: '이익잉여금 이입',
    normalBalance: 'credit',
    requiresApproval: false,
  },
  {
    code: '9011',
    name: 'Appropriation to Legal Reserve',
    category: 'equity',
    subCategory: 'retained',
    level: 2,
    isActive: true,
    description: '법정준비금 적립',
    normalBalance: 'credit',
    requiresApproval: false,
    parentCode: '9010',
  },
  {
    code: '9012',
    name: 'Dividend Distribution',
    category: 'equity',
    subCategory: 'retained',
    level: 2,
    isActive: true,
    description: '배당금 지급',
    normalBalance: 'debit',
    requiresApproval: true,
    approvalThreshold: 10000000,
    parentCode: '9010',
  },
];

// 표준 계정체계 (allowsTransactions 자동 추가)
export const K_IFRS_CHART_OF_ACCOUNTS: ChartOfAccounts = {
  version: '2.0.0',
  standard: 'K-IFRS',
  effectiveDate: new Date('2024-01-01'),
  accounts: processAccounts(RAW_ACCOUNTS),
};

// ============================================
// 🔧 계정체계 관리 클래스
// ============================================

export class ChartOfAccountsManager {
  private static instance: ChartOfAccountsManager;
  private chartOfAccounts: ChartOfAccounts;

  private constructor() {
    this.chartOfAccounts = K_IFRS_CHART_OF_ACCOUNTS;
  }

  static getInstance(): ChartOfAccountsManager {
    if (!ChartOfAccountsManager.instance) {
      ChartOfAccountsManager.instance = new ChartOfAccountsManager();
    }
    return ChartOfAccountsManager.instance;
  }

  /**
   * 계정코드로 계정 정보 조회
   */
  getAccountByCode(code: string): StandardAccount | undefined {
    return this.chartOfAccounts.accounts.find((account) => account.code === code);
  }

  /**
   * 카테고리별 계정 목록 조회
   */
  getAccountsByCategory(category: StandardAccount['category']): StandardAccount[] {
    return this.chartOfAccounts.accounts.filter((account) => account.category === category);
  }

  /**
   * 모든 계정 목록 조회
   */
  getAllAccounts(): StandardAccount[] {
    return this.chartOfAccounts.accounts;
  }

  /**
   * 부모 계정의 하위 계정들 조회
   */
  getChildAccounts(parentCode: string): StandardAccount[] {
    return this.chartOfAccounts.accounts.filter((account) => account.parentCode === parentCode);
  }

  /**
   * 계정코드 유효성 검증
   */
  validateAccountCode(code: string): boolean {
    return this.getAccountByCode(code) !== undefined;
  }

  /**
   * 승인 필요 여부 확인
   */
  requiresApproval(code: string, amount: number): boolean {
    const account = this.getAccountByCode(code);
    if (!account) return false;

    if (!account.requiresApproval) return false;
    if (!account.approvalThreshold) return false;

    return amount >= account.approvalThreshold;
  }

  /**
   * 계정의 정상 잔액 방향 확인
   */
  getNormalBalance(code: string): 'debit' | 'credit' | undefined {
    const account = this.getAccountByCode(code);
    return account?.normalBalance;
  }

  /**
   * 계정 계층 구조 조회
   */
  getAccountHierarchy(code: string): StandardAccount[] {
    const account = this.getAccountByCode(code);
    if (!account) return [];

    const hierarchy: StandardAccount[] = [account];

    // 부모 계정들 추가
    let currentParent = account.parentCode;
    while (currentParent) {
      const parentAccount = this.getAccountByCode(currentParent);
      if (parentAccount) {
        hierarchy.unshift(parentAccount);
        currentParent = parentAccount.parentCode;
      } else {
        break;
      }
    }

    return hierarchy;
  }

  /**
   * ✅ 거래 가능 여부 확인 (Leaf Account 검증)
   *
   * ⚠️ 중요: 상위 계정 (level 1)은 집계용만 사용, 실제 거래 불가
   * - level 1: false (집계용, 거래 불가)
   * - level ≥ 2: true (실제 거래 가능)
   *
   * @param code 계정 코드
   * @returns 거래 가능 여부
   * @throws Error 계정이 존재하지 않거나 비활성화된 경우
   */
  canUseForTransaction(code: string): boolean {
    const account = this.getAccountByCode(code);

    if (!account) {
      throw new Error(`계정 코드 '${code}'를 찾을 수 없습니다.`);
    }

    if (!account.isActive) {
      throw new Error(`계정 코드 '${code}'는 비활성화된 계정입니다.`);
    }

    return account.allowsTransactions;
  }

  /**
   * ✅ Leaf Account (말단 계정) 여부 확인
   *
   * Leaf Account는 실제 거래에 사용 가능한 계정입니다.
   *
   * @param code 계정 코드
   * @returns Leaf Account 여부
   */
  isLeafAccount(code: string): boolean {
    const account = this.getAccountByCode(code);
    return account?.allowsTransactions ?? false;
  }

  /**
   * ✅ 거래 가능한 계정 목록 조회 (Leaf Accounts만)
   *
   * @param category 선택적 카테고리 필터
   * @returns 거래 가능한 계정 목록
   */
  getTransactionAccounts(category?: StandardAccount['category']): StandardAccount[] {
    let accounts = this.chartOfAccounts.accounts.filter((account) => account.allowsTransactions);

    if (category) {
      accounts = accounts.filter((account) => account.category === category);
    }

    return accounts;
  }
}

// ============================================
// 🎯 계정 코드 상수 (하드코딩 방지)
// ============================================

/**
 * Physical Space용 Expense 계정 코드 매핑
 * expenseType → 계정 코드
 */
export const PHYSICAL_SPACE_EXPENSE_ACCOUNT_MAP: Record<string, string> = {
  rent: '6010', // Rent Expense (임차료)
  utilities: '6020', // Utilities (수도광열비)
  maintenance: '6040', // Repairs and Maintenance (수선비)
  depreciation: '6030', // Depreciation Expense (감가상각비)
  supplies: '6050', // Office Supplies (소모품비)
  other: '6090', // Other Operating Expenses (기타영업비용)
};

/**
 * Physical Space용 Revenue 계정 코드
 */
export const PHYSICAL_SPACE_RENTAL_INCOME_ACCOUNT_CODE = '4020'; // Rental Income

/**
 * 기본 계정 코드 상수
 */
export const DEFAULT_ACCOUNT_CODES = {
  CASH: '1000', // Cash and Cash Equivalents (집계용)
  CASH_ON_HAND: '1001', // Cash on Hand
  CASH_IN_BANK: '1002', // Cash in Bank
  ACCOUNTS_RECEIVABLE: '1010', // Accounts Receivable (집계용)
  TRADE_RECEIVABLES: '1011', // Trade Receivables
  NOTES_RECEIVABLE: '1020', // Notes Receivable (집계용)
  ACCOUNTS_PAYABLE: '2000', // Accounts Payable (집계용)
  TRADE_PAYABLES: '2001', // Trade Payables
  INVENTORY: '1030', // Inventory (집계용)
  SALES_REVENUE: '4000', // Sales Revenue (집계용)
  SERVICE_REVENUE: '4010', // Service Revenue (집계용)
  RENTAL_INCOME: '4020', // Rental Income (집계용)
  RAW_MATERIALS_USED: '5000', // Raw Materials Used (집계용)
  OTHER_OPERATING_EXPENSES: '6090', // Other Operating Expenses (집계용)
} as const;

/**
 * expenseType에서 계정 코드 조회 (Physical Space용)
 *
 * @param expenseType - expenseType ('rent', 'utilities', 'maintenance', 'depreciation', 'supplies', 'other')
 * @returns 계정 코드
 */
export function getPhysicalSpaceExpenseAccountCode(expenseType: string): string {
  const accountCode = PHYSICAL_SPACE_EXPENSE_ACCOUNT_MAP[expenseType];
  return accountCode || PHYSICAL_SPACE_EXPENSE_ACCOUNT_MAP['other'] || '6090';
}

/**
 * Physical Space의 Rental Income 계정 코드 조회
 *
 * @returns 계정 코드 ('4020')
 */
export function getPhysicalSpaceRentalIncomeAccountCode(): string {
  return PHYSICAL_SPACE_RENTAL_INCOME_ACCOUNT_CODE;
}

export default ChartOfAccountsManager;
